import type { UtmParameters } from '@shared/core/router.helper/RoutesContext';

/**
 * Partial UTM parameters type for flexible usage.
 * Allows partial UTM parameters (not all three required).
 */
export type PartialUtmParameters = Partial<UtmParameters & { readonly utm_source_secondary?: string }>;

/**
 * `utm_source` values Joy stamps on its own internal navigation — not acquisition channels.
 * Excludes outbound-share sources (`joy`, `room_block_manager_share`) and the post-RSVP /
 * registry-confirmation sources the data team owns. Keep in sync when adding an internal CTA.
 */
export const INTERNAL_UTM_SOURCES = [
  // Guest site
  'website_accommodations_page',
  'website_travel_page',
  'website_custom_page',
  'website_hotel_redirect_page',
  'custom_page_booking_assistant',
  'guest_guide_booking_cta',
  'guest_guide_guest_site',
  'schedule_list_item',
  'hotel_search',
  'hotel_details',
  'hotel_joy_native_pdp',
  'hotel_confirmation',
  // Admin / host surfaces that deep-link into the same hotel routes
  'admin_custom_page',
  'admin_accommodations',
  'admin_accommodations_joy_roomblock',
  'accommodations_admin_page',
  'dashboard',
  'manage_registry',
] as const;

/** A `utm_source` value stamped by Joy's own internal navigation. */
export type InternalUtmSource = (typeof INTERNAL_UTM_SOURCES)[number];

/** Whether a `utm_source` was stamped by Joy's own internal navigation rather than an acquisition channel. */
export const isInternalUtmSource = (utmSource?: string | null): boolean => {
  return !!utmSource && (INTERNAL_UTM_SOURCES as ReadonlyArray<string>).includes(utmSource);
};

/**
 * Resolves the UTM an internal CTA should emit, per slot: inbound wins for `utm_medium`,
 * `utm_campaign` and `utm_source_secondary`, but an internal `utm_source` yields to the CTA's own.
 * No equivalent classification exists for `utm_campaign` — see TRAV-3930.
 */
export const resolveAcquisitionAwareUtm = (currentUtm: PartialUtmParameters | undefined, internalUtm: PartialUtmParameters): PartialUtmParameters => {
  const inboundSource = isInternalUtmSource(currentUtm?.utm_source) ? undefined : currentUtm?.utm_source;

  const utm_medium = currentUtm?.utm_medium || internalUtm.utm_medium;
  const utm_source = inboundSource || internalUtm.utm_source;
  const utm_campaign = currentUtm?.utm_campaign || internalUtm.utm_campaign;
  const utm_source_secondary = currentUtm?.utm_source_secondary || internalUtm.utm_source_secondary;

  // Conditional spreads, not `{...internalUtm}` — an explicit `key: undefined` survives a spread and
  // callers that serialise the whole object would emit the literal string "undefined".
  return {
    ...(utm_medium && { utm_medium }),
    ...(utm_source && { utm_source }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_source_secondary && { utm_source_secondary }),
  };
};

/**
 * Merges UTM parameters with priority: existing parameters override internal parameters.
 *
 * This function merges two sets of UTM parameters where existing parameters (typically from URL)
 * take precedence over internal parameters (typically defaults). This ensures that campaign/ads
 * tracking parameters are preserved and not overridden by internal defaults.
 *
 * @param existingUtm - UTM parameters from existing URL (highest priority - overrides internal)
 * @param internalUtm - Internal UTM parameters to use as fallback (lower priority)
 * @returns Merged UTM parameters object with existing values taking precedence
 */
export const mergeUtmParameters = (existingUtm?: PartialUtmParameters, internalUtm?: PartialUtmParameters): PartialUtmParameters => {
  // Spread internal first (base), then existing (overrides) - existing takes precedence
  return { ...internalUtm, ...existingUtm };
};

/**
 * Builds a UTM query string from the provided UTM parameters.
 * https://withjoy.atlassian.net/wiki/x/CgAw3g
 *
 * @param utm_medium - The utm_medium through which the traffic is coming (e.g. "web", "email", "text")
 * @param utm_source - The utm_source of the traffic (e.g. "website_accommodations_page", "confirmation_rsvp", "confirmation_registry_purchase")
 * @param utm_campaign - The utm_campaign of the marketing campaign (e.g. "explore_other_options", "post_rsvp_hotel_grid", "next_steps")
 * @returns {string} The formatted UTM query string (e.g. "utm_medium=web&utm_source=website_accommodations_page&utm_campaign=explore_other_options")
 */

/**
 * Extracts UTM parameters from a URL string.
 * Returns undefined if the URL is unparseable or contains no UTM params.
 * Unknown `utm_medium` values (outside `'web' | 'email' | 'text'`) are dropped.
 *
 * @param url - The URL to extract UTM parameters from
 * @returns Partial UTM parameters object, or undefined if no recognized UTMs exist
 */
export const extractUtmParametersFromUrl = (url: string): PartialUtmParameters | undefined => {
  try {
    const parsed = new URL(url);
    const utm_medium = parsed.searchParams.get('utm_medium');
    const utm_source = parsed.searchParams.get('utm_source');
    const utm_campaign = parsed.searchParams.get('utm_campaign');
    const utm_source_secondary = parsed.searchParams.get('utm_source_secondary');

    const result: PartialUtmParameters = {
      ...(utm_medium && ['web', 'email', 'text'].includes(utm_medium) ? { utm_medium: utm_medium as 'web' | 'email' | 'text' } : {}),
      ...(utm_source && { utm_source }),
      ...(utm_campaign && { utm_campaign }),
      ...(utm_source_secondary && { utm_source_secondary }),
    };
    return Object.keys(result).length > 0 ? result : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Applies UTM parameters to a URL string.
 * Merges UTM parameters into the target URL, preserving existing query parameters.
 *
 * @param url - The target URL to apply UTM parameters to
 * @param utm - Partial UTM parameters to apply
 * @returns The URL with UTM parameters merged in
 */
export const applyUtmParametersToUrl = (url: string, utm: PartialUtmParameters): string => {
  try {
    const urlObj = new URL(url);
    if (utm.utm_medium) {
      urlObj.searchParams.set('utm_medium', utm.utm_medium);
    }
    if (utm.utm_source) {
      urlObj.searchParams.set('utm_source', utm.utm_source);
    }
    if (utm.utm_campaign) {
      urlObj.searchParams.set('utm_campaign', utm.utm_campaign);
    }
    return urlObj.toString();
  } catch {
    // If URL parsing fails, try simple string concatenation for relative URLs
    const separator = url.includes('?') ? '&' : '?';
    const params: string[] = [];
    if (utm.utm_medium) {
      params.push(`utm_medium=${encodeURIComponent(utm.utm_medium)}`);
    }
    if (utm.utm_source) {
      params.push(`utm_source=${encodeURIComponent(utm.utm_source)}`);
    }
    if (utm.utm_campaign) {
      params.push(`utm_campaign=${encodeURIComponent(utm.utm_campaign)}`);
    }
    return params.length > 0 ? `${url}${separator}${params.join('&')}` : url;
  }
};
